(function () {
"use strict";

class CollectionLayout extends HTMLElement {
  constructor() {
    super();
    this.layoutWrap = null;
    this.panels = [];
    this.buttons = [];
    this.filterToggles = [];
    this.filterHide = null;
    this.filterHeader = null;
    this.filters = null;
    this.drawer = null;
    this.drawerPanel = null;
    this.drawerOverlay = null;
    this.drawerContent = null;
    this.drawerCloseButtons = [];
    this.drawerApplyCount = null;
    this.filtersForm = null;
    this.resultsContainer = null;
    this.paginationContainer = null;
    this.loadingOverlay = null;
    this.sortToggle = null;
    this.sortMenu = null;
    this.sortLabel = null;
    this.sortOptions = [];
    this.compareToggle = null;
    this.compareOpenButton = null;
    this.compareExitButton = null;
    this.compareCount = null;
    this.compareBar = null;
    this.compareBarList = null;
    this.compareBarOpen = null;
    this.compareBarCount = null;
    this.compareModal = null;
    this.compareModalContent = null;
    this.compareModalLabels = null;
    this.compareModalColumns = null;
    this.compareModalCloseButtons = [];
    this.compareButtons = [];
    this.compareItems = [];
    this.compareMode = false;
    this.compareModalOpen = false;
    this.compareMax = 4;
    this.activeSortValue = "";
    this.drawerOpener = null;
    this._drawerFocusTrap = null;
    this.compareOpener = null;
    this._compareFocusTrap = null;
    this.activeController = null;
    this.isLoading = false;
    this.storageKey = "collection-layout";
    this.desktopFiltersVisible = true;
    this.onFilterToggle = this.onFilterToggle.bind(this);
    this.onFilterHide = this.onFilterHide.bind(this);
    this.onLayoutClick = this.onLayoutClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onFilterChange = this.onFilterChange.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onPaginationClick = this.onPaginationClick.bind(this);
    this.onFilterLinkClick = this.onFilterLinkClick.bind(this);
    this.onSortToggle = this.onSortToggle.bind(this);
    this.onSortOptionClick = this.onSortOptionClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onDrawerClose = this.onDrawerClose.bind(this);
    this.onCompareToggle = this.onCompareToggle.bind(this);
    this.onCompareOpen = this.onCompareOpen.bind(this);
    this.onCompareExit = this.onCompareExit.bind(this);
    this.onCompareSelect = this.onCompareSelect.bind(this);
    this.onCompareRemove = this.onCompareRemove.bind(this);
    this.onCompareModalClose = this.onCompareModalClose.bind(this);
  }

  connectedCallback() {
    this.layoutWrap = this.querySelector("[data-collection-layout]");
    this.panels = Array.from(
      this.querySelectorAll("[data-collection-layout-panel]")
    );
    this.buttons = Array.from(
      this.querySelectorAll("[data-collection-layout-button]")
    );
    this.filterToggles = Array.from(
      this.querySelectorAll("[data-collection-filter-toggle]")
    );
    this.filters = this.querySelector("[data-collection-filters]");
    this.drawer = this.querySelector("[data-collection-filter-drawer]");
    this.drawerPanel = this.querySelector("[data-collection-filter-panel]");
    this.drawerOverlay = this.querySelector("[data-collection-filter-overlay]");
    this.drawerContent = this.querySelector(
      "[data-collection-filter-drawer-content]"
    );
    this.drawerCloseButtons = Array.from(
      this.querySelectorAll("[data-collection-filter-close]")
    );
    this.drawerApplyCount = this.querySelector("[data-collection-apply-count]");
    this.compareToggle = this.querySelector("[data-collection-compare-toggle]");

    this.refreshDynamicBindings(); // 初始化动态绑定，如过滤表单和分页容器
    if (this.panels.length && this.buttons.length) {
      const storedLayout = this.getStoredLayout();
      let defaultLayout =
        storedLayout ||
        this.dataset.defaultLayout ||
        this.buttons[0].dataset.layout;
      if (!this.isDesktop() && defaultLayout === "row") {
        this._mobileLayoutOverride = true;
        defaultLayout = "card";
      }
      this.setLayout(defaultLayout, { persist: !this._mobileLayoutOverride });
    }

    if (this.filters && this.filterToggles.length) {
      this.desktopFiltersVisible = this.getDefaultFiltersVisible();
      this.setFiltersVisible(this.desktopFiltersVisible);
      this.filterToggles.forEach((toggle) => {
        toggle.addEventListener("click", this.onFilterToggle);
      });
    }
    this.refreshFilterControls();
    this.updateDrawerApplyCount();

    if (this.buttons.length) {
      this.buttons.forEach((button) => {
        button.addEventListener("click", this.onLayoutClick);
      });
    }
    this.refreshCompareBindings();
    window.addEventListener("resize", this.onResize);
  }

  disconnectedCallback() {
    this.filterToggles.forEach((toggle) => {
      toggle.removeEventListener("click", this.onFilterToggle);
    });
    this.teardownFilterControls();
    this.teardownDynamicBindings();
    this.buttons.forEach((button) => {
      button.removeEventListener("click", this.onLayoutClick);
    });
    this.teardownCompareBindings();
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onDocumentKeydown);
    this.unlockScroll();
  }

  onFilterToggle(event) {
    if (this.isDesktop()) {
      if (!this.filters) return;
      this.setFiltersVisible(this.filters.classList.contains("hidden"));
      return;
    }
    if (!this.drawer) return;
    const isOpen = this.drawer.getAttribute("aria-hidden") === "false";
    if (!isOpen) {
      this.drawerOpener = event?.currentTarget || document.activeElement;
    }
    this.setDrawerOpen(!isOpen);
  }

  onFilterHide() {
    if (!this.filters) return;
    this.setFiltersVisible(false);
  }

  onLayoutClick(event) {
    const button = event.currentTarget;
    if (!button?.dataset?.layout) return;
    this.setLayout(button.dataset.layout);
  }

  onFilterChange(event) {
    if (!this.filtersForm) return;
    const target = event.target;
    if (target && target.disabled) return;
    this.updateDrawerApplyCount();
    const params = this.determineSearchParams("form");
    this.syncHistoryState(params);
    const focusState = this.captureFilterFocus(target);
    this.fetchAndRender(params, { restoreFilterFocus: focusState });
  }

  onFormSubmit(event) {
    if (!this.filtersForm) return;
    event.preventDefault();
    const params = this.determineSearchParams("form");
    this.syncHistoryState(params);
    this.fetchAndRender(params);
  }

  onPaginationClick(event) {
    const link = event.target.closest("a");
    if (!link || !this.paginationContainer?.contains(link)) return;
    event.preventDefault();
    const params = this.determineSearchParams("link", link);
    this.syncHistoryState(params);
    this.fetchAndRender(params, { focusResults: true });
  }

  onFilterLinkClick(event) {
    const link = event.target.closest("[data-collection-filter-link]");
    if (!link || !this.contains(link)) return;
    event.preventDefault();
    const params = this.determineSearchParams("link", link);
    this.syncHistoryState(params);
    this.fetchAndRender(params);
  }

  onSortToggle() {
    if (!this.sortMenu || !this.sortToggle) return;
    const isOpen = !this.sortMenu.classList.contains("hidden");
    this.setSortMenuOpen(!isOpen);
  }

  onSortOptionClick(event) {
    const option = event.currentTarget;
    const value = option?.dataset?.sortValue;
    if (!value) return;
    this.activeSortValue = value;
    this.updateSortUI(value);
    this.setSortMenuOpen(false);
    const params = this.determineSearchParams("form");
    this.syncHistoryState(params);
    this.fetchAndRender(params);
  }

  onDocumentClick(event) {
    if (!this.sortMenu || !this.sortToggle) return;
    if (this.contains(event.target)) {
      if (
        this.sortMenu.contains(event.target) ||
        this.sortToggle.contains(event.target)
      ) {
        return;
      }
    }
    this.setSortMenuOpen(false);
  }

  onDocumentKeydown(event) {
    if (event.key !== "Escape") return;
    this.setSortMenuOpen(false);
  }

  onResize() {
    if (this.isDesktop()) {
      this.setFiltersVisible(this.desktopFiltersVisible);
      if (this._mobileLayoutOverride) {
        this._mobileLayoutOverride = false;
        const restored = this.getStoredLayout() || this.dataset.defaultLayout;
        if (restored) this.setLayout(restored);
      }
    } else {
      this.setFiltersVisible(false);
      this.setDrawerOpen(false);
      if (this.activeLayout === "row") {
        this._mobileLayoutOverride = true;
        this.setLayout("card", { persist: false });
      }
    }
  }

  onDrawerClose() {
    this.setDrawerOpen(false);
  }

  onCompareToggle() {
    this.setCompareMode(!this.compareMode);
  }

  onCompareOpen(event) {
    if (!this.compareItems.length) return;
    this.compareOpener = event?.currentTarget || document.activeElement;
    this.setCompareModalOpen(true);
  }

  onCompareExit() {
    this.setCompareMode(false);
  }

  onCompareSelect(event) {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget;
    const card = button.closest("[data-compare-item]");
    if (!card) return;
    const item = this.getCompareItemFromCard(card);
    if (!item || !item.id) return;
    const index = this.compareItems.findIndex((entry) => entry.id === item.id);
    if (index >= 0) {
      this.compareItems.splice(index, 1);
    } else if (this.compareItems.length < this.compareMax) {
      this.compareItems.push(item);
    }
    this.updateCompareUI();
  }

  onCompareRemove(event) {
    const removeButton = event.target.closest("[data-compare-remove]");
    if (!removeButton) return;
    const id = removeButton.dataset.compareId;
    if (!id) return;
    const index = this.compareItems.findIndex((entry) => entry.id === id);
    if (index >= 0) {
      this.compareItems.splice(index, 1);
    }
    this.updateCompareUI();
  }

  onCompareModalClose() {
    this.setCompareModalOpen(false);
  }

  isDesktop() {
    return window.matchMedia("(min-width: 1024px)").matches;
  }

  getDefaultFiltersVisible() {
    const expandedByDefault =
      this.dataset.filterPanelExpandedByDefault ||
      this.filters?.dataset.filterPanelExpandedByDefault;
    return expandedByDefault !== "false";
  }

  // 设置当前布局并更新视图
  setLayout(layout, { persist = true } = {}) {
    this.activeLayout = layout;
    if (persist) this.storeLayout(layout);
    this.panels.forEach((panel) => {
      const isActive = panel.dataset.layout === layout;
      panel.classList.toggle("hidden", !isActive);
    });
    this.buttons.forEach((button) => {
      const isActive = button.dataset.layout === layout;
      button.dataset.active = isActive ? "true" : "false";
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  // 从本地存储中获取布局偏好
  getStoredLayout() {
    try {
      return window.localStorage.getItem(this.storageKey) || "";
    } catch (error) {
      return "";
    }
  }

  // 将布局偏好存储在本地存储中
  storeLayout(layout) {
    if (!layout) return;
    try {
      window.localStorage.setItem(this.storageKey, layout);
    } catch (error) {}
  }

  // 设置过滤器面板的可见性
  setFiltersVisible(visible) {
    if (this.isDesktop()) {
      this.desktopFiltersVisible = visible;
    }
    const shouldShow = this.isDesktop() && visible;
    if (this.filters) {
      this.filters.classList.toggle("hidden", !shouldShow);
      this.filters.classList.toggle("lg:hidden", !shouldShow);
      this.filters.classList.toggle("lg:block", shouldShow);
    }
    const desktopToggle = this.getDesktopToggle();
    if (desktopToggle) {
      desktopToggle.classList.toggle("hidden", shouldShow);
      desktopToggle.setAttribute(
        "aria-expanded",
        shouldShow ? "true" : "false"
      );
    }
    if (this.layoutWrap) {
      this.layoutWrap.classList.toggle(
        "lg:grid-cols-[360px_minmax(0,1fr)]",
        shouldShow
      );
      this.layoutWrap.classList.toggle("lg:grid-cols-1", !shouldShow);
    }
  }

  // 设置抽屉的打开状态
  setDrawerOpen(isOpen) {
    if (!this.drawer) return;
    this.drawer.classList.toggle("opacity-100", isOpen);
    this.drawer.classList.toggle("opacity-0", !isOpen);
    this.drawer.classList.toggle("pointer-events-none", !isOpen);
    this.drawer.setAttribute("aria-hidden", isOpen ? "false" : "true");
    if (this.drawerOverlay) {
      this.drawerOverlay.classList.toggle("opacity-100", isOpen);
      this.drawerOverlay.classList.toggle("opacity-0", !isOpen);
      this.drawerOverlay.classList.toggle("pointer-events-auto", isOpen);
      this.drawerOverlay.classList.toggle("pointer-events-none", !isOpen);
    }
    if (this.drawerPanel) {
      const isRtl = document.documentElement.getAttribute("dir") === "rtl";
      this.drawerPanel.classList.toggle("translate-x-0", isOpen);
      this.drawerPanel.classList.toggle("-translate-x-full", !isOpen && !isRtl);
      this.drawerPanel.classList.toggle("translate-x-full", !isOpen && isRtl);
    }
    const drawerToggle = this.getDrawerToggle();
    if (drawerToggle) {
      drawerToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    }
    if (isOpen) {
      this.lockScroll();
      if (!this._drawerFocusTrap && this.drawer) {
        this._drawerFocusTrap = webvista.createFocusTrap(this.drawer, {
          onEscape: this.onDrawerClose,
        });
      }
      if (this._drawerFocusTrap && this.drawer) {
        this.drawer.addEventListener("keydown", this._drawerFocusTrap);
      }
      webvista.focusFirstElement(this.drawer);
    } else {
      this.unlockScroll();
      if (this._drawerFocusTrap && this.drawer) {
        this.drawer.removeEventListener("keydown", this._drawerFocusTrap);
      }
      if (this.drawerOpener && typeof this.drawerOpener.focus === "function") {
        this.drawerOpener.focus();
        this.drawerOpener = null;
      }
    }
    if (isOpen) {
      this.updateDrawerApplyCount();
    }
  }

  // 获取桌面过滤切换按钮
  getDesktopToggle() {
    if (!this.filters) return null;
    const id = this.filters.id;
    return (
      this.filterToggles.find(
        (toggle) => toggle.getAttribute("aria-controls") === id
      ) || null
    );
  }

  // 获取抽屉过滤切换按钮
  getDrawerToggle() {
    if (!this.drawer) return null;
    const id = this.drawer.id;
    return (
      this.filterToggles.find(
        (toggle) => toggle.getAttribute("aria-controls") === id
      ) || null
    );
  }

  getActiveFiltersForm() {
    if (this.isDesktop()) {
      return this.filters?.querySelector("[data-collection-filters-form]");
    }
    if (this.drawer) {
      return this.drawer.querySelector("[data-collection-filters-form]");
    }
    return this.querySelector("[data-collection-filters-form]");
  }

  getFilterSurface(element) {
    return element?.closest("[data-collection-filter-drawer]")
      ? "drawer"
      : "desktop";
  }

  getFilterSurfaceRoot(surface) {
    return surface === "drawer" ? this.drawerContent : this.filters;
  }

  captureFilterFocus(target = document.activeElement) {
    if (!(target instanceof HTMLElement)) return null;
    const group = target.closest("[data-filter-param-name]");
    if (!group || !this.contains(group)) return null;
    return {
      surface: this.getFilterSurface(group),
      paramName: group.dataset.filterParamName || "",
      name: target.getAttribute("name") || "",
      value: target.getAttribute("data-filter-value") || target.getAttribute("value") || "",
      role:
        target.getAttribute("data-price-input-min") !== null
          ? "price-min"
          : target.getAttribute("data-price-input-max") !== null
            ? "price-max"
            : target.matches("[data-accordion-trigger]")
              ? "trigger"
              : "input",
    };
  }

  captureFilterUiState() {
    const state = {};
    ["desktop", "drawer"].forEach((surface) => {
      const root = this.getFilterSurfaceRoot(surface);
      if (!root) return;
      const groups = {};
      root.querySelectorAll("[data-filter-param-name]").forEach((group) => {
        const paramName = group.dataset.filterParamName;
        if (!paramName || Object.hasOwn(groups, paramName)) return;
        groups[paramName] = group.hasAttribute("data-open");
      });
      state[surface] = groups;
    });
    return state;
  }

  restoreFilterUiState(state = {}) {
    ["desktop", "drawer"].forEach((surface) => {
      const root = this.getFilterSurfaceRoot(surface);
      const groups = state[surface];
      if (!root || !groups) return;
      root.querySelectorAll("[data-filter-param-name]").forEach((group) => {
        const paramName = group.dataset.filterParamName;
        if (!paramName || !Object.hasOwn(groups, paramName)) return;
        const accordion = group.closest("accordion-details");
        if (accordion && typeof accordion.setItemOpen === "function") {
          accordion.setItemOpen(group, groups[paramName]);
        }
      });
    });
  }

  restoreFilterFocus(focusState) {
    if (!focusState?.paramName) return;
    const root = this.getFilterSurfaceRoot(focusState.surface);
    if (!root) return;
    const group = Array.from(
      root.querySelectorAll("[data-filter-param-name]")
    ).find((item) => item.dataset.filterParamName === focusState.paramName);
    if (!group) return;

    let target = null;
    if (focusState.role === "trigger") {
      target = group.querySelector("[data-accordion-trigger]");
    } else if (focusState.role === "price-min") {
      target = group.querySelector("[data-price-input-min]");
    } else if (focusState.role === "price-max") {
      target = group.querySelector("[data-price-input-max]");
    } else {
      target = Array.from(group.querySelectorAll("input")).find((input) => {
        const inputValue =
          input.getAttribute("data-filter-value") ||
          input.getAttribute("value") ||
          "";
        return (
          input.getAttribute("name") === focusState.name &&
          inputValue === focusState.value
        );
      });
    }
    if (target) target.focus({ preventScroll: true });
  }

  // 动态绑定过滤表单和分页容器的事件监听器
  // 以便在内容更新后重新绑定
  refreshDynamicBindings() {
    this.teardownDynamicBindings(); // 先移除旧的绑定
    this.filtersForm = this.getActiveFiltersForm();
    this.resultsContainer = this.querySelector("[data-collection-results]");
    this.paginationContainer = this.querySelector(
      "[data-collection-pagination]"
    );
    this.loadingOverlay = this.querySelector("[data-collection-loading]");
    this.sortToggle = this.querySelector("[data-collection-sort-toggle]");
    this.sortMenu = this.querySelector("[data-collection-sort-menu]");
    this.sortLabel = this.querySelector("[data-collection-sort-label]");
    this.sortOptions = Array.from(
      this.querySelectorAll("[data-collection-sort-option]")
    );
    this.drawerApplyCount = this.querySelector("[data-collection-apply-count]");

    if (this.filtersForm) {
      this.filtersForm.addEventListener("change", this.onFilterChange);
      this.filtersForm.addEventListener("submit", this.onFormSubmit);
    }
    if (this.sortToggle) {
      this.sortToggle.addEventListener("click", this.onSortToggle);
    }
    if (this.sortOptions.length) {
      this.sortOptions.forEach((option) => {
        option.addEventListener("click", this.onSortOptionClick);
      });
    }
    this.syncSortFromUrl();
    this.updateDrawerApplyCount();
    this.addEventListener("click", this.onFilterLinkClick);
    if (this.paginationContainer) {
      this.paginationContainer.addEventListener(
        "click",
        this.onPaginationClick
      );
    }
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onDocumentKeydown);
  }

  teardownDynamicBindings() {
    if (this.filtersForm) {
      this.filtersForm.removeEventListener("change", this.onFilterChange);
      this.filtersForm.removeEventListener("submit", this.onFormSubmit);
    }
    if (this.sortToggle) {
      this.sortToggle.removeEventListener("click", this.onSortToggle);
    }
    if (this.sortOptions.length) {
      this.sortOptions.forEach((option) => {
        option.removeEventListener("click", this.onSortOptionClick);
      });
    }
    this.removeEventListener("click", this.onFilterLinkClick);
    if (this.paginationContainer) {
      this.paginationContainer.removeEventListener(
        "click",
        this.onPaginationClick
      );
    }
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onDocumentKeydown);
  }

  refreshFilterControls() {
    this.teardownFilterControls();
    this.filterHide = this.querySelector("[data-collection-filter-hide]");
    this.filterHeader = this.querySelector("[data-collection-filters-header]");
    this.drawerCloseButtons = Array.from(
      this.querySelectorAll("[data-collection-filter-close]")
    );
    if (this.filters && this.filterHide) {
      this.filterHide.addEventListener("click", this.onFilterHide);
    }
    if (this.drawerCloseButtons.length) {
      this.drawerCloseButtons.forEach((button) => {
        button.addEventListener("click", this.onDrawerClose);
      });
    }
  }

  teardownFilterControls() {
    if (this.filterHide) {
      this.filterHide.removeEventListener("click", this.onFilterHide);
    }
    if (this.drawerCloseButtons.length) {
      this.drawerCloseButtons.forEach((button) => {
        button.removeEventListener("click", this.onDrawerClose);
      });
    }
  }

  refreshCompareBindings() {
    this.teardownCompareBindings();
    this.compareToggle = this.querySelector("[data-collection-compare-toggle]");
    this.compareOpenButton = this.querySelector(
      "[data-collection-compare-open]"
    );
    this.compareExitButton = this.querySelector(
      "[data-collection-compare-exit]"
    );
    this.compareCount = this.querySelector("[data-collection-compare-count]");
    this.compareBar = this.querySelector("[data-compare-bar]");
    this.compareBarList = this.querySelector("[data-compare-bar-list]");
    this.compareBarOpen = this.querySelector("[data-compare-bar-open]");
    this.compareBarCount = this.querySelector("[data-compare-bar-count]");
    this.compareModal = document.querySelector("[data-compare-modal]");
    this.compareModalOverlay = this.compareModal
      ? this.compareModal.querySelector(".modal__overlay")
      : null;
    this.compareModalContent = document.querySelector(
      "[data-compare-modal-content]"
    );
    this.compareModalLabels = document.querySelector(
      "[data-compare-modal-labels]"
    );
    this.compareModalColumns = document.querySelector(
      "[data-compare-modal-columns]"
    );
    this.compareColumnTemplate = document.querySelector(
      "[data-compare-column-template]"
    );
    this.comparePlaceholder = document.querySelector(
      "[data-compare-placeholder]"
    );
    this.compareModalCloseButtons = Array.from(
      document.querySelectorAll("[data-compare-modal-close]")
    );
    this.compareButtons = Array.from(
      this.querySelectorAll("[data-compare-select]")
    );

    if (this.compareToggle) {
      this.compareToggle.addEventListener("click", this.onCompareToggle);
    }
    if (this.compareOpenButton) {
      this.compareOpenButton.addEventListener("click", this.onCompareOpen);
    }
    if (this.compareExitButton) {
      this.compareExitButton.addEventListener("click", this.onCompareExit);
    }
    if (this.compareBarOpen) {
      this.compareBarOpen.addEventListener("click", this.onCompareOpen);
    }
    if (this.compareBar) {
      this.compareBar.addEventListener("click", this.onCompareRemove);
    }
    if (this.compareModalContent) {
      this.compareModalContent.addEventListener("click", this.onCompareRemove);
    }
    if (this.compareModalCloseButtons.length) {
      this.compareModalCloseButtons.forEach((button) => {
        button.addEventListener("click", this.onCompareModalClose);
      });
    }
    if (this.compareButtons.length) {
      this.compareButtons.forEach((button) => {
        button.addEventListener("click", this.onCompareSelect);
      });
    }
    this.setCompareMode(this.compareMode);
  }

  teardownCompareBindings() {
    if (this.compareToggle) {
      this.compareToggle.removeEventListener("click", this.onCompareToggle);
    }
    if (this.compareOpenButton) {
      this.compareOpenButton.removeEventListener("click", this.onCompareOpen);
    }
    if (this.compareExitButton) {
      this.compareExitButton.removeEventListener("click", this.onCompareExit);
    }
    if (this.compareBarOpen) {
      this.compareBarOpen.removeEventListener("click", this.onCompareOpen);
    }
    if (this.compareBar) {
      this.compareBar.removeEventListener("click", this.onCompareRemove);
    }
    if (this.compareModalContent) {
      this.compareModalContent.removeEventListener(
        "click",
        this.onCompareRemove
      );
    }
    if (this.compareModalCloseButtons.length) {
      this.compareModalCloseButtons.forEach((button) => {
        button.removeEventListener("click", this.onCompareModalClose);
      });
    }
    if (this.compareButtons.length) {
      this.compareButtons.forEach((button) => {
        button.removeEventListener("click", this.onCompareSelect);
      });
    }
  }

  determineSearchParams(type, target = null) {
    if (type === "link" && target?.href) {
      const url = new URL(target.href, window.location.origin);
      return url.searchParams.toString();
    }
    if (!this.filtersForm) return "";
    const params = new URLSearchParams();
    const formData = new FormData(this.filtersForm);
    formData.forEach((value, key) => {
      if (value === null || value === "") return;
      params.append(key, value);
    });
    const sortValue =
      this.activeSortValue ||
      this.sortToggle?.dataset?.sortValue ||
      this.sortOptions[0]?.dataset?.sortValue ||
      "";
    if (sortValue) {
      params.set("sort_by", sortValue);
    } else {
      params.delete("sort_by");
    }
    return params.toString();
  }

  syncHistoryState(params) {
    const path = window.location.pathname;
    const nextUrl = params ? `${path}?${params}` : path;
    window.history.pushState({ params }, "", nextUrl);
  }

  buildRequestUrl(params) {
    const path = window.location.pathname;
    return params ? `${path}?${params}` : path;
  }

  scrollToResults() {
    const target = this.resultsContainer || this;
    const offset = window.innerHeight * 0.4;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }

  focusFirstProduct() {
    if (!this.resultsContainer) return;
    // Wait for DOM paint so the freshly-injected product cards are focusable
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const firstLink = this.resultsContainer.querySelector(
          ".product-card__title-link"
        );
        if (firstLink) {
          firstLink.focus({ preventScroll: true });
        } else {
          // Fallback: focus the results container itself
          if (!this.resultsContainer.hasAttribute("tabindex")) {
            this.resultsContainer.setAttribute("tabindex", "-1");
          }
          this.resultsContainer.focus({ preventScroll: true });
        }
      });
    });
  }

  setLoadingState(isLoading) {
    this.isLoading = isLoading;
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.toggle("opacity-100", isLoading);
      this.loadingOverlay.classList.toggle("pointer-events-auto", isLoading);
      this.loadingOverlay.setAttribute(
        "aria-hidden",
        isLoading ? "false" : "true"
      );
    }
    if (this.resultsContainer) {
      this.resultsContainer.classList.toggle("opacity-60", isLoading);
      this.resultsContainer.setAttribute(
        "aria-busy",
        isLoading ? "true" : "false"
      );
    }
  }

  async fetchAndRender(params, options = {}) {
    const { restoreFilterFocus = null, focusResults = false } = options;
    if (this.activeController) {
      this.activeController.abort();
    }
    const controller = new AbortController();
    this.activeController = controller;
    this.setLoadingState(true);

    try {
      // Filter UI re-renders replace hovered elements without firing mouseleave/blur,
      // so proactively hide any visible tooltip to avoid it "sticking" on screen.
      if (window.webvista?.hideTooltips) {
        window.webvista.hideTooltips();
      }

      const requestUrl = this.buildRequestUrl(params);
      const response = await fetch(requestUrl, {
        signal: controller.signal,
        headers: { Accept: "text/html" }
      });
      if (!response.ok) {
        throw new Error(
          `Request failed: ${response.status} ${response.statusText}`
        );
      }
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const filterUiState = this.captureFilterUiState();

      this.updateViewFragment(doc, "[data-collection-results]");
      this.updateViewFragment(doc, "[data-collection-filters]");
      this.updateViewFragment(doc, "[data-collection-filter-drawer-content]");
      this.updateViewFragment(doc, "[data-collection-pagination]");
      this.updateProductsCount(doc);
      this.updateFilterCount(doc);
      this.restoreFilterUiState(filterUiState);

      this.panels = Array.from(
        this.querySelectorAll("[data-collection-layout-panel]")
      );
      this.setLayout(this.activeLayout || this.dataset.defaultLayout, { persist: !this._mobileLayoutOverride });
      this.refreshDynamicBindings();
      this.refreshFilterControls();
      this.refreshCompareBindings();
      this.syncSortFromUrl();

      if (window.webvista?.initLazyImageAnimation && this.resultsContainer) {
        window.webvista.initLazyImageAnimation(this.resultsContainer);
      }
      if (window.webvista?.initTooltips) {
        if (this.filters) window.webvista.initTooltips(this.filters);
        if (this.drawerContent) window.webvista.initTooltips(this.drawerContent);
      }

      // Restore focus by stable filter identity after the server HTML is applied.
      this.restoreFilterFocus(restoreFilterFocus);

      // Scroll to the top of collection results after content update
      this.scrollToResults();

      // After pagination, move focus to the first product card for keyboard users
      if (focusResults) {
        this.focusFirstProduct();
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        this.handleError(error);
      }
    } finally {
      if (this.activeController === controller) {
        this.activeController = null;
      }
      this.setLoadingState(false);
    }
  }

  updateViewFragment(doc, selector) {
    const source = doc.querySelector(selector);
    const target = this.querySelector(selector);
    if (!target) return;
    target.innerHTML = source ? source.innerHTML : "";
  }

  updateProductsCount(doc) {
    const source = doc.querySelector("[data-collection-products-count]");
    const target = this.querySelector("[data-collection-products-count]");
    if (source && target) {
      target.textContent = source.textContent;
    }
  }

  updateFilterCount(doc) {
    const sources = doc.querySelectorAll("[data-collection-filter-count]");
    const targets = this.querySelectorAll("[data-collection-filter-count]");
    targets.forEach((target, i) => {
      if (sources[i]) {
        target.textContent = sources[i].textContent;
        target.classList.toggle("hidden", sources[i].classList.contains("hidden"));
      }
    });
  }

  handleError(error) {
    if (!this.resultsContainer) return;
    const errorMsg = this.dataset.loadingError || "Failed to load, please try again.";
    this.resultsContainer.innerHTML =
      `<div class="collection-results__error text-sm text-foreground/70">${errorMsg}</div>`;
    console.error(error);
  }

  syncSortFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const sortValue =
      params.get("sort_by") ||
      this.sortToggle?.dataset?.sortValue ||
      this.sortOptions[0]?.dataset?.sortValue ||
      "";
    if (!sortValue) return;
    this.activeSortValue = sortValue;
    this.updateSortUI(sortValue);
  }

  updateSortUI(value) {
    if (this.sortToggle) {
      this.sortToggle.dataset.sortValue = value;
    }
    this.sortOptions.forEach((option) => {
      const isActive = option.dataset.sortValue === value;
      option.dataset.active = isActive ? "true" : "false";
      const check = option.querySelector("[data-sort-check]");
      if (check) {
        check.classList.toggle("hidden", !isActive);
      }
    });
    if (this.sortLabel) {
      const match = this.sortOptions.find(
        (option) => option.dataset.sortValue === value
      );
      if (match?.dataset?.sortLabel) {
        this.sortLabel.textContent = match.dataset.sortLabel;
      }
    }
  }

  setSortMenuOpen(isOpen) {
    if (!this.sortMenu || !this.sortToggle) return;
    this.sortMenu.classList.toggle("hidden", !isOpen);
    this.sortToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    const icon = this.sortToggle.querySelector(".icon-rotate");
    if (icon) {
      icon.classList.toggle("rotate-180", isOpen);
    }
  }

  setCompareMode(isActive) {
    this.compareMode = Boolean(isActive);
    if (this.compareToggle) {
      this.compareToggle.dataset.active = this.compareMode ? "true" : "false";
      this.compareToggle.setAttribute(
        "aria-pressed",
        this.compareMode ? "true" : "false"
      );
      this.compareToggle.classList.toggle("hidden", this.compareMode);
    }
    if (this.compareOpenButton) {
      this.compareOpenButton.classList.toggle("hidden", !this.compareMode);
    }
    if (this.compareExitButton) {
      this.compareExitButton.classList.toggle("hidden", !this.compareMode);
    }
    if (!this.compareMode) {
      this.setCompareModalOpen(false);
    }
    this.updateCompareUI();
  }

  setCompareModalOpen(isOpen) {
    if (!this.compareModal) return;
    this.compareModalOpen = Boolean(isOpen);
    this.compareModal.classList.toggle("hidden", !this.compareModalOpen);
    this.compareModal.classList.toggle("flex", this.compareModalOpen);
    this.compareModal.setAttribute(
      "aria-hidden",
      this.compareModalOpen ? "false" : "true"
    );
    if (this.compareModalOpen) {
      if (this.compareModalOverlay) {
        requestAnimationFrame(() => {
          if (!this.compareModalOverlay) return;
          this.compareModalOverlay.classList.add("opacity-100");
          this.compareModalOverlay.classList.remove("opacity-0");
        });
      }
      this._compareSavedScrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${this._compareSavedScrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.documentElement.classList.add("overflow-hidden");
      if (!this._compareFocusTrap) {
        this._compareFocusTrap = webvista.createFocusTrap(this.compareModal, {
          onEscape: this.onCompareModalClose,
        });
      }
      this.compareModal.addEventListener("keydown", this._compareFocusTrap);
      this.renderCompareModal();
      webvista.focusFirstElement(this.compareModal);
      return;
    }
    document.documentElement.classList.remove("overflow-hidden");
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    if (typeof this._compareSavedScrollY === "number") {
      window.scrollTo(0, this._compareSavedScrollY);
      this._compareSavedScrollY = null;
    }
    if (this._compareFocusTrap) {
      this.compareModal.removeEventListener("keydown", this._compareFocusTrap);
    }
    if (this.compareOpener && typeof this.compareOpener.focus === "function") {
      this.compareOpener.focus();
      this.compareOpener = null;
    }
    if (this.compareModalOverlay) {
      this.compareModalOverlay.classList.add("opacity-0");
      this.compareModalOverlay.classList.remove("opacity-100");
    }
  }

  updateCompareUI() {
    const count = this.compareItems.length;
    if (this.compareCount) {
      this.compareCount.textContent = `(${count})`;
    }
    if (this.compareOpenButton) {
      this.compareOpenButton.disabled = count === 0;
    }
    if (this.compareBarCount) {
      const tpl = this.dataset.compareSelectedText || `${count} / ${this.compareMax} Selected`;
      this.compareBarCount.textContent = tpl.replace("__COUNT__", count).replace("__MAX__", this.compareMax);
    }
    this.updateCompareButtons();
    this.renderCompareBar();
    if (this.compareModalOpen && count === 0) {
      this.setCompareModalOpen(false);
      return;
    }
    if (this.compareModalOpen) {
      this.renderCompareModal();
    }
  }

  updateCompareButtons() {
    if (!this.compareButtons.length) return;
    const maxReached = this.compareItems.length >= this.compareMax;
    this.compareButtons.forEach((button) => {
      const card = button.closest("[data-compare-item]");
      const id = card?.dataset?.compareId;
      const isSelected = Boolean(
        id && this.compareItems.find((entry) => entry.id === id)
      );
      button.classList.toggle("hidden", !this.compareMode);
      button.classList.toggle("bg-accent", isSelected);
      button.classList.toggle("text-accent_contrast", isSelected);
      button.classList.toggle("border-accent", isSelected);
      const check = button.querySelector("[data-compare-check]");
      if (check) {
        check.classList.toggle("hidden", !isSelected);
      }
      const shouldDisable = !isSelected && maxReached;
      button.disabled = shouldDisable;
      button.classList.toggle("cursor-not-allowed", shouldDisable);
      button.classList.toggle("opacity-30", shouldDisable && !isSelected);
    });
  }

  renderCompareBar() {
    if (!this.compareBar || !this.compareBarList) return;
    const shouldShow = this.compareMode && this.compareItems.length > 0;
    this.compareBar.classList.toggle("hidden", !shouldShow);
    if (!shouldShow) return;
    const removeLabelTpl = this.compareBar.dataset.compareRemoveLabel || "Remove __TITLE__";
    const thumbs = this.compareItems
      .map((item) => {
        const image = this.escapeHtml(item.image || "");
        const title = this.escapeHtml(item.title || "");
        const id = this.escapeHtml(item.id || "");
        const removeLabel = removeLabelTpl.replace("__TITLE__", title);
        return `
          <button
            type="button"
            class="relative w-10 h-10 rounded-full border-2 border-background overflow-hidden shadow-sm transition-transform hover:-translate-y-1 bg-background group cursor-pointer"
            data-compare-remove
            data-compare-id="${id}"
            aria-label="${removeLabel}"
            title="${removeLabel}"
          >
            ${
              image
                ? `<img src="${image}" alt="${title}" class="w-full h-full object-cover">`
                : ""
            }
            <span class="absolute inset-0 bg-foreground/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <span class="w-3 h-3 text-background">
                ${this.getCloseIconSvg()}
              </span>
            </span>
          </button>
        `;
      })
      .join("");
    this.compareBarList.innerHTML = thumbs;
  }

  renderCompareModal() {
    if (
      !this.compareModalLabels ||
      !this.compareModalColumns ||
      !this.compareColumnTemplate
    )
      return;
    const items = this.compareItems;
    const optionNames = this.getCompareOptionNames();

    this.updateCompareOptionLabels(optionNames);
    this.compareModalColumns.innerHTML = "";
    items.forEach((item) => {
      this.compareModalColumns.appendChild(
        this.renderCompareColumn(item, optionNames)
      );
    });
    if (this.comparePlaceholder) {
      this.comparePlaceholder.classList.toggle(
        "hidden",
        items.length >= this.compareMax
      );
    }
    if (window.webvista?.initTooltips) {
      window.webvista.initTooltips(this.compareModal);
    }
  }

  updateCompareOptionLabels(optionNames) {
    if (!this.compareModal) return;
    const container = this.compareModal.querySelector(
      "[data-compare-option-labels]"
    );
    const template = this.compareModal.querySelector(
      "[data-compare-option-label-template]"
    );
    if (!container || !template) return;
    container.innerHTML = "";
    optionNames.forEach((name) => {
      const node = template.content.firstElementChild.cloneNode(true);
      node.textContent = name;
      container.appendChild(node);
    });
  }

  renderCompareColumn(item, optionNames) {
    const title = item.title || "";
    const price = item.price || "-";
    const image = item.image || "";
    const sku = item.sku || "-";
    const tag = item.tag || "";
    const optionMap = new Map(
      (item.options || []).map((option) => [option.name, option])
    );
    const swatchMap = item.swatches || {};
    const node =
      this.compareColumnTemplate.content.firstElementChild.cloneNode(true);
    const removeButton = node.querySelector("[data-compare-column-remove]");
    const imageEl = node.querySelector("[data-compare-column-image]");
    const titleEl = node.querySelector("[data-compare-column-title]");
    const tagEl = node.querySelector("[data-compare-column-tag]");
    const rowsEl = node.querySelector("[data-compare-column-rows]");
    const viewDetails = node.querySelector("[data-compare-view-details]");

    if (removeButton) {
      removeButton.dataset.compareRemove = "";
      removeButton.dataset.compareId = item.id || "";
      const removeLabelTpl = this.compareBar?.dataset?.compareRemoveLabel || "Remove __TITLE__";
      removeButton.setAttribute(
        "aria-label",
        removeLabelTpl.replace("__TITLE__", this.escapeHtml(title))
      );
    }
    if (imageEl) {
      if (image) {
        imageEl.src = image;
        imageEl.alt = title;
        imageEl.classList.remove("hidden");
      } else {
        imageEl.classList.add("hidden");
      }
    }
    if (titleEl) {
      titleEl.textContent = title;
    }
    if (tagEl) {
      if (tag) {
        tagEl.textContent = tag;
        tagEl.classList.remove("hidden");
      } else {
        tagEl.textContent = "";
        tagEl.classList.add("hidden");
      }
    }
    if (viewDetails) {
      viewDetails.setAttribute("href", item.url || "#");
    }

    if (rowsEl) {
      const priceRow = rowsEl.querySelector('[data-compare-row="price"]');
      if (priceRow) {
        priceRow.textContent = price;
      }
      const skuRow = rowsEl.querySelector('[data-compare-row="sku"]');
      if (skuRow) {
        skuRow.textContent = sku;
      }
      const optionRows = rowsEl.querySelector("[data-compare-option-rows]");
      const optionTemplate = rowsEl.querySelector(
        "[data-compare-option-row-template]"
      );
      if (optionRows && optionTemplate) {
        optionRows.innerHTML = "";
        optionNames.forEach((name) => {
          const optionEntry = optionMap.get(name);
          const optionRow =
            optionTemplate.content.firstElementChild.cloneNode(true);
          if (optionEntry && optionEntry.values.length) {
            optionEntry.values.slice(0, 5).forEach((optionValue) => {
              const color = swatchMap[optionValue];
              if (color) {
                const swatchEl = document.createElement("span");
                swatchEl.className =
                  "compare-modal__swatch w-6 h-6 rounded-full ring-1 ring-foreground/20";
                swatchEl.style.background = color;
                swatchEl.setAttribute("data-tooltip", optionValue);
                swatchEl.setAttribute("aria-label", optionValue);
                optionRow.appendChild(swatchEl);
                return;
              }
              const textEl = document.createElement("span");
              textEl.className =
                "compare-modal__chip text-xs bg-foreground/5 px-2 py-1 rounded-sm text-foreground/70";
              textEl.textContent = optionValue;
              optionRow.appendChild(textEl);
            });
          }
          optionRows.appendChild(optionRow);
        });
      }
    }
    return node;
  }

  getCompareItemFromCard(card) {
    if (!card?.dataset) return null;
    return {
      id: card.dataset.compareId || "",
      title: card.dataset.compareTitle || "",
      url: card.dataset.compareUrl || "",
      image: card.dataset.compareImage || "",
      price: card.dataset.comparePrice || "",
      sku: card.dataset.compareSku || "",
      options: this.parseCompareOptions(card.dataset.compareOptions || ""),
      swatches: this.parseCompareSwatches(card.dataset.compareSwatches || ""),
      tag: card.dataset.compareTag || ""
    };
  }

  parseCompareOptions(raw) {
    if (!raw) return [];
    const decoded = this.decodeHtml(raw);
    return decoded
      .split("||")
      .map((entry) => {
        const parts = entry.split("::");
        const valueText = (parts[1] || "").trim();
        return {
          name: (parts[0] || "").trim(),
          valueText,
          values: valueText
            ? valueText.split(",").map((value) => value.trim())
            : []
        };
      })
      .filter((entry) => entry.name);
  }

  parseCompareSwatches(raw) {
    if (!raw) return {};
    const decoded = this.decodeHtml(raw);
    return decoded
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean)
      .reduce((acc, entry) => {
        const [label, color] = entry.split("::").map((part) => part.trim());
        if (label && color) {
          acc[label] = color;
        }
        return acc;
      }, {});
  }

  getCompareFields() {
    const raw =
      this.compareModal?.dataset?.compareFields || "price,options,sku";
    return raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  getCompareOptionNames() {
    const names = [];
    const seen = new Set();
    this.compareItems.forEach((item) => {
      (item.options || []).forEach((option) => {
        if (!option.name || seen.has(option.name)) return;
        seen.add(option.name);
        names.push(option.name);
      });
    });
    return names;
  }

  getCompareRows() {
    const fields = this.getCompareFields();
    const rows = [];
    const priceLabel = this.compareModal?.dataset?.compareLabelPrice || "Price";
    const skuLabel = this.compareModal?.dataset?.compareLabelSku || "SKU";
    const optionNames = this.getCompareOptionNames();
    fields.forEach((field) => {
      if (field === "price") {
        rows.push({ type: "price", label: priceLabel });
      } else if (field === "options") {
        optionNames.forEach((name) => {
          rows.push({ type: "option", label: name, key: name });
        });
      } else if (field === "sku") {
        rows.push({ type: "sku", label: skuLabel });
      }
    });
    return rows;
  }

  decodeHtml(value) {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = String(value);
    return textarea.value;
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  getCloseIconSvg() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-full h-full">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    `;
  }

  getPlusIconSvg() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-8 h-8">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
      </svg>
    `;
  }

  updateDrawerApplyCount() {
    if (!this.drawer || !this.drawerApplyCount) return;
    const form = this.drawer.querySelector("[data-collection-filters-form]");
    if (!form) return;
    const checkedBoxes = form.querySelectorAll(
      "input[type='checkbox']:checked"
    ).length;
    const rangeMin = form.querySelector("[data-price-range-min]");
    const rangeMax = form.querySelector("[data-price-range-max]");
    const toNumber = (value, fallback = 0) => {
      const parsed = Number.parseFloat(value);
      return Number.isNaN(parsed) ? fallback : parsed;
    };
    let rangeChanged = false;
    if (rangeMin && rangeMax) {
      const minValue = toNumber(rangeMin.value, toNumber(rangeMin.min));
      const maxValue = toNumber(rangeMax.value, toNumber(rangeMax.max));
      const minDefault = toNumber(rangeMin.min);
      const maxDefault = toNumber(rangeMax.max);
      rangeChanged = minValue !== minDefault || maxValue !== maxDefault;
    }
    const total = checkedBoxes + (rangeChanged ? 1 : 0);
    if (total > 0) {
      this.drawerApplyCount.textContent = `(${total})`;
      this.drawerApplyCount.classList.remove("hidden");
    } else {
      this.drawerApplyCount.textContent = "";
      this.drawerApplyCount.classList.add("hidden");
    }
  }

  lockScroll() {
    this._savedScrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${this._savedScrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.documentElement.classList.add("overflow-hidden");
  }

  unlockScroll() {
    document.documentElement.classList.remove("overflow-hidden");
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    if (typeof this._savedScrollY === "number") {
      window.scrollTo(0, this._savedScrollY);
      this._savedScrollY = null;
    }
  }
}

if (!customElements.get("collection-layout")) {
  customElements.define("collection-layout", CollectionLayout);
}

// 价格区间组件
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.rangeMin = this.querySelector("[data-price-range-min]");
    this.rangeMax = this.querySelector("[data-price-range-max]");
    this.inputMin = this.querySelector("[data-price-input-min]");
    this.inputMax = this.querySelector("[data-price-input-max]");
    this.progress = this.querySelector("[data-price-range-progress]");

    if (!this.rangeMin || !this.rangeMax) return;
    this.debounceMs = 1000;
    this.emitTimer = null;
    this.toNumber =
      window.webvista?.toNumber ||
      ((value, fallback = 0) => {
        const parsed = Number.parseFloat(value);
        return Number.isNaN(parsed) ? fallback : parsed;
      });
    this.clampNumber =
      window.webvista?.clampNumber ||
      ((value, min, max) => Math.min(Math.max(value, min), max));

    this.boundOnRangeMinChange = this.onRangeMinChange.bind(this);
    this.boundOnRangeMaxChange = this.onRangeMaxChange.bind(this);
    this.boundOnInputMinChange = this.onInputMinChange.bind(this);
    this.boundOnInputMaxChange = this.onInputMaxChange.bind(this);
    this.boundStopEmit = this.stopEmit.bind(this);

    this.initBlockNativeChange();

    this.rangeMin.addEventListener("input", this.boundOnRangeMinChange);
    this.rangeMax.addEventListener("input", this.boundOnRangeMaxChange);
    if (this.inputMin) {
      this.inputMin.addEventListener("input", this.boundStopEmit);
      this.inputMin.addEventListener("change", this.boundOnInputMinChange);
    }
    if (this.inputMax) {
      this.inputMax.addEventListener("input", this.boundStopEmit);
      this.inputMax.addEventListener("change", this.boundOnInputMaxChange);
    }

    this.updateProgressFromRanges();
  }

  disconnectedCallback() {
    if (this.rangeMin && this.boundOnRangeMinChange) {
      this.rangeMin.removeEventListener("input", this.boundOnRangeMinChange);
    }
    if (this.rangeMax && this.boundOnRangeMaxChange) {
      this.rangeMax.removeEventListener("input", this.boundOnRangeMaxChange);
    }
    if (this.inputMin && this.boundOnInputMinChange) {
      this.inputMin.removeEventListener("change", this.boundOnInputMinChange);
    }
    if (this.inputMax && this.boundOnInputMaxChange) {
      this.inputMax.removeEventListener("change", this.boundOnInputMaxChange);
    }
    if (this.boundStopEmit) {
      if (this.inputMin) {
        this.inputMin.removeEventListener("input", this.boundStopEmit);
      }
      if (this.inputMax) {
        this.inputMax.removeEventListener("input", this.boundStopEmit);
      }
    }
    this.stopEmit();
    this.removeBlockNativeChange();
  }

  initBlockNativeChange() {
    this.boundBlockNativeChange = (event) => event.stopPropagation();
    [this.rangeMin, this.rangeMax, this.inputMin, this.inputMax]
      .filter(Boolean)
      .forEach((element) =>
        element.addEventListener("change", this.boundBlockNativeChange)
      );
  }

  removeBlockNativeChange() {
    [this.rangeMin, this.rangeMax, this.inputMin, this.inputMax]
      .filter(Boolean)
      .forEach((element) =>
        element.removeEventListener("change", this.boundBlockNativeChange)
      );
  }

  scheduleEmit() {
    this.stopEmit();
    this.emitTimer = setTimeout(() => {
      this.dispatchEvent(new Event("change", { bubbles: true }));
    }, this.debounceMs);
  }

  onRangeMinChange() {
    const minVal = this.toNumber(this.rangeMin.value);
    const maxVal = this.toNumber(this.rangeMax.value, minVal);
    const minGap = 1;

    if (minVal >= maxVal) {
      this.rangeMin.value = String(
        Math.max(maxVal - minGap, Number(this.rangeMin.min))
      );
    }

    if (this.inputMin) this.inputMin.value = this.rangeMin.value;

    this.updateProgressFromRanges();
    this.scheduleEmit();
  }

  onRangeMaxChange() {
    const maxVal = this.toNumber(this.rangeMax.value);
    const minVal = this.toNumber(this.rangeMin.value, maxVal);
    const minGap = 1;

    if (maxVal <= minVal) {
      this.rangeMax.value = String(
        Math.min(minVal + minGap, Number(this.rangeMax.max))
      );
    }

    if (this.inputMax) this.inputMax.value = this.rangeMax.value;

    this.updateProgressFromRanges();
    this.scheduleEmit();
  }

  stopEmit() {
    if (this.emitTimer) {
      clearTimeout(this.emitTimer);
      this.emitTimer = null;
    }
  }

  onInputMinChange(event) {
    let value = this.toNumber(event.currentTarget.value);
    const min = this.toNumber(this.rangeMin.min);
    const max = this.toNumber(this.rangeMax.value);
    const minGap = 1;

    value = this.clampNumber(value, min, max - minGap);

    this.rangeMin.value = String(value);
    event.currentTarget.value = String(value);

    this.updateProgressFromRanges();
    this.scheduleEmit();
  }

  onInputMaxChange(event) {
    let value = this.toNumber(event.currentTarget.value);
    const min = this.toNumber(this.rangeMin.value);
    const max = this.toNumber(this.rangeMax.max);
    const minGap = 1;

    value = this.clampNumber(value, min + minGap, max);

    this.rangeMax.value = String(value);
    event.currentTarget.value = String(value);

    this.updateProgressFromRanges();
    this.scheduleEmit();
  }

  updateProgressFromRanges() {
    if (!this.progress) return;
    const minRange = this.toNumber(this.rangeMin.min);
    const maxRange = this.toNumber(this.rangeMin.max, minRange);
    const span = maxRange - minRange || 1;
    const minVal = this.toNumber(this.rangeMin.value, minRange);
    const maxVal = this.toNumber(this.rangeMax.value, maxRange);
    const start = ((minVal - minRange) * 100) / span;
    const end = ((maxVal - minRange) * 100) / span;

    this.progress.style.setProperty("--start", `${start}%`);
    this.progress.style.setProperty("--end", `${end}%`);
  }
}

if (!customElements.get("price-range")) {
  customElements.define("price-range", PriceRange);
}

// Countdown Banner Component
class CountdownBanner extends HTMLElement {
  constructor() {
    super();
    this.timer = null;
    this.totalDuration = null;
  }

  connectedCallback() {
    const end = Number.parseInt(this.dataset.countdownEnd, 10);
    if (!Number.isFinite(end)) return;
    const start = Number.parseInt(this.dataset.countdownStart, 10);
    const now = Math.floor(Date.now() / 1000);
    const startValue = Number.isFinite(start) ? start : now;
    this.totalDuration = Math.max(1, end - startValue);
    this.update(end);
    this.timer = setInterval(() => this.update(end), 1000);
  }

  disconnectedCallback() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  update(end) {
    const display = this.querySelector("[data-countdown-display]");
    const progress = this.querySelector("[data-countdown-progress]");
    if (!display) return;

    const now = Math.floor(Date.now() / 1000);
    const remaining = Math.max(0, end - now);
    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;
    display.textContent = `${String(hours).padStart(2, "0")}:${String(
      minutes
    ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

    if (progress && this.totalDuration) {
      const ratio = Math.min(1, Math.max(0, remaining / this.totalDuration));
      progress.style.width = `${Math.round(ratio * 100)}%`;
    }

    if (remaining === 0 && this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

if (!customElements.get("countdown-banner")) {
  customElements.define("countdown-banner", CountdownBanner);
}

})();
